import type { VendorFilterChip, VendorFilterChipRemoval } from '../lib/vendorFilterChips.ts';

/**
 * The Vendors list's active filters, as removable chips plus a clear-all.
 *
 * Why this exists: the only clear-filters control used to live inside the
 * zero-rows empty state, so a filter matching SOME rows left the admin with no
 * visible summary and no one-click escape — just a quiet "10 of 22 vendors" in
 * the header. A stale filter restored from localStorage then reads as a data
 * bug rather than a filter.
 *
 * Purely presentational: it takes chips from vendorFilterChips() and hands the
 * caller back the `remove` instruction to apply, so the router and the filter
 * setters stay in Companies.tsx.
 *
 * Chip styling follows ExpiryChip's rounded-full / text-xs language so the row
 * reads as the same family as the rest of the admin.
 */
export function ActiveFilterChips({
  chips,
  onRemove,
  onClearAll,
}: {
  chips: VendorFilterChip[];
  onRemove: (removal: VendorFilterChipRemoval) => void;
  onClearAll: () => void;
}) {
  // No filter, no row — the search/dropdown row above keeps its own spacing.
  if (chips.length === 0) return null;

  return (
    <div
      role="group"
      aria-label="Active filters"
      className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Filters</span>

      {chips.map((chip) => (
        <span
          key={chip.id}
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2 pr-1 text-xs font-medium text-slate-700"
        >
          <span className="text-slate-500">{chip.label}:</span>
          <span>{chip.value}</span>
          <button
            type="button"
            // Named, not a bare "Remove": with several chips on the row a
            // screen reader would otherwise announce identical buttons.
            aria-label={`Remove ${chip.label} filter ${chip.value}`}
            onClick={() => onRemove(chip.remove)}
            className="flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700"
          >
            <span aria-hidden="true">×</span>
          </button>
        </span>
      ))}

      <button
        type="button"
        onClick={onClearAll}
        className="text-xs font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900"
      >
        Clear all
      </button>
    </div>
  );
}
